import { FC, useEffect, useRef, useState } from "react";
import { Box, Breadcrumbs, Button, Card, Container, Grid, Link, Typography, useTheme } from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import ChevronRightIcon from "src/icons/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import CurrencyTable from "./CurrencyTable";
import * as Yup from 'yup';
import { getCurrencies } from "../applicationSettings/ConnectionService";
import { getCurrencyMaster, insertUpdateCurrency } from "./ConnectionService";

type CurrencyTableColumnProps = {
  width?: number | string;
  headerName: string;
  field: string;
  editable: boolean;
  rowKey?: string | number;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  inputType?: "select" | "text";
  options?: any[];
  dataValueField?: string | null;
  dataTextField?: string | null;
  handleChange?: any;
  listKey?: string;
  required?: boolean;
};

const defaultForm = {
  CurrencyKey: null,
  Country: null,
  CurrencyName: null,
  CurrencyCode: null,
  CurrencyCode2: null
};

const validationSchema = Yup.object().shape({
  Country: Yup.string().nullable().required('Country is required'),
  CurrencyName: Yup.string().nullable().required('Currency is required'),
  CurrencyCode2: Yup.string().nullable().max(10, 'At max 10 characters').matches(/^[A-Za-z]+$/, 'Invalid input')
});

const CurrencyPage: FC = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currencyData, setCurrencyData] = useState([]);
  const [currencyMaster, setCurrencyMaster] = useState([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [currencyDataOriginal, setCurrencyDataOriginal] = useState([]);
  const [filteredCurrencyData, setFilteredCurrencyData] = useState([]);
  const formRef = useRef(null);

  const handleCountryChange = (value) => {
    const updatedData = currencyData.map((x, idx) => {
      if (idx === editingRow) {
        return {
          CurrencyKey: x.CurrencyKey,
          Country: value.Country ?? null,
          CurrencyName: value.Currency ?? null,
          CurrencyCode: value.CurrencyCode ?? null,
          CurrencyCode2: x.CurrencyCode2,
        };
      } else {
        return x;
      }
    });
    formRef?.current?.setValues(updatedData[editingRow]);
    if (value) {
      const filteredCurrencies = currencyMaster.filter(x => x.Country === value.Country);
      setFilteredCurrencyData(filteredCurrencies);
    } else {
      setFilteredCurrencyData([]);
    }
    setCurrencyData((prev) => updatedData);
  };

  const tableColumns: CurrencyTableColumnProps[] = [
    {
      headerName: 'Country',
      field: 'Country',
      editable: true,
      align: 'left',
      inputType: 'select',
      options: currencyMaster,
      dataValueField: 'Country',
      dataTextField: 'Country',
      handleChange: handleCountryChange,
      listKey: 'CurrencyMasterKey',
      required: true
    },
    {
      headerName: 'Currency',
      field: 'CurrencyName',
      editable: true,
      inputType: 'select',
      options: filteredCurrencyData,
      dataValueField: 'Currency',
      dataTextField: 'Currency',
      required: true,
      width: '350',
      align: 'left',
    },
    {
      headerName: 'Currency Code',
      field: 'CurrencyCode',
      editable: false,
      align: 'center',
      width: '200'
    },
    {
      headerName: 'Currency Code 2',
      field: 'CurrencyCode2',
      editable: true,
      inputType: 'text',
      align: 'center',
      width: '200'
    },
  ];

  const getCurrencyData = async () => {
    setIsLoading(true);
    const res = await getCurrencies();
    const updatedData = res.map(x => ({
      CurrencyKey: x.DefaultCurrency,
      Country: x.Country,
      CurrencyName: x.CurrencyName,
      CurrencyCode: x.CurrencyCode,
      CurrencyCode2: x.CurrencyCode2,
    }));
    setCurrencyData(updatedData);
    setCurrencyDataOriginal(updatedData);
    setIsLoading(false);
  };

  const getCurrencyMasterData = async () => {
    const res = await getCurrencyMaster();
    setCurrencyMaster(res);
  };

  const handleEditClick = (rowIdx, page, rowsPerPage) => {
    const value = currencyData.slice(page * rowsPerPage,page * rowsPerPage + rowsPerPage).at(rowIdx);
    const filteredCurrencies = currencyMaster.filter(x => x.Country === value.Country);
    setFilteredCurrencyData(filteredCurrencies);
    formRef?.current?.setValues({
      CurrencyKey: value.CurrencyKey ?? null,
      Country: value.Country ?? null,
      CurrencyName: value.Currency ?? null,
      CurrencyCode: value.CurrencyCode ?? null,
      CurrencyCode2: formRef?.current?.values?.CurrencyCode2 ?? null
    });
    setEditingRow(rowIdx);
  };

  const handleSave = async (index: number, values: any) => {
    const res = await insertUpdateCurrency(values);
    if (res === true) {
      getCurrencyData();
      setFilteredCurrencyData([]);
      setEditingRow(null);
    }
  };

  const handleCancelClick = () => {
    setEditingRow(null);
    setCurrencyData(currencyDataOriginal);
  };

  const handleAddCurrency = () => {
    setCurrencyData((prev) => [defaultForm, ...prev]);
    setEditingRow(0);
  };

  useEffect(() => {
    getCurrencyData();
    getCurrencyMasterData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Currency</title>
      </Helmet>
      <Box>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              container
              alignItems="center"
              sx={{ my: 2 }}
              justifyContent="space-between"
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Currency
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Button
                      disabled={isLoading || editingRow !== null}
                      onClick={handleAddCurrency}
                      variant="contained"
                      sx={{
                        background: "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100,
                      }}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CurrencyTable
              columns={tableColumns}
              dataList={currencyData}
              isLoading={isLoading}
              handleSave={handleSave}
              handleCancel={handleCancelClick}
              initialForm={defaultForm}
              editingRow={editingRow}
              formValidationSchema={validationSchema}
              formRef={formRef}
              onClickEdit={handleEditClick}
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default CurrencyPage;
